import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { Lock, User } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { ZynxLogo } from '@/shared/components/brand/ZynxLogo';
import { useAuthActions, useIsAuthenticated } from '@/shared/hooks/useAuth';
import { getApiErrorMessage } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';

const schema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});
type LoginForm = z.infer<typeof schema>;

const EASE = [0.22, 1, 0.36, 1] as const;

export function LoginPage() {
  const isAuthenticated = useIsAuthenticated();
  const { login } = useAuthActions();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitting, setSubmitting] = useState(false);
  const reduce = useReducedMotion();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(schema) });

  if (isAuthenticated) {
    const from = (location.state as { from?: Location })?.from?.pathname ?? ROUTES.dashboard;
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      await login(data);
      toast.success('Welcome back!');
      const from = (location.state as { from?: Location })?.from?.pathname ?? ROUTES.dashboard;
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Login failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  const container: Variants = {
    hidden: {},
    show: { transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: reduce ? 0 : 0.15 } },
  };
  const item: Variants = {
    hidden: reduce ? { opacity: 0 } : { opacity: 0, y: 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#080a12] p-4">
      {/* Aurora background */}
      <div className="pointer-events-none absolute inset-0">
        <motion.div
          className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/30 blur-[120px]"
          animate={reduce ? undefined : { x: [0, 40, 0], y: [0, 30, 0] }}
          transition={{ duration: 16, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute -bottom-40 -right-24 h-[28rem] w-[28rem] rounded-full bg-indigo-500/20 blur-[130px]"
          animate={reduce ? undefined : { x: [0, -50, 0], y: [0, -20, 0] }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-amber-500/10 blur-[120px]"
          animate={reduce ? undefined : { opacity: [0.4, 0.8, 0.4] }}
          transition={{ duration: 10, repeat: Infinity, repeatType: 'mirror', ease: 'easeInOut' }}
        />
        {/* faint grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)',
            backgroundSize: '44px 44px',
          }}
        />
      </div>

      {/* Content */}
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="relative z-10 flex w-full max-w-sm flex-col items-center"
      >
        {/* Brand */}
        <motion.div
          variants={item}
          className="mb-7 flex flex-col items-center gap-4"
        >
          <motion.div
            initial={reduce ? { opacity: 0 } : { opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <ZynxLogo tone="light" markClassName="h-14 w-14 text-2xl" wordClassName="text-[2.25rem]" tagline />
          </motion.div>
          <p className="text-body-sm text-white/55">Enterprise CRM Workspace</p>
        </motion.div>

        {/* Card */}
        <motion.div
          variants={item}
          className="w-full rounded-card border border-white/10 bg-white/95 p-7 shadow-surface-3 backdrop-blur-xl"
        >
          <div className="mb-5">
            <h1 className="text-headline-md font-semibold text-on-surface">Sign in</h1>
            <p className="mt-0.5 text-body-sm text-on-surface-variant">Welcome back — access your workspace.</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
            <motion.div variants={item}>
              <Input
                label="Username"
                placeholder="you@durranitech.com"
                autoComplete="username"
                leftIcon={<User className="h-4 w-4" />}
                error={errors.username?.message}
                {...register('username')}
              />
            </motion.div>
            <motion.div variants={item}>
              <Input
                label="Password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                leftIcon={<Lock className="h-4 w-4" />}
                error={errors.password?.message}
                {...register('password')}
              />
            </motion.div>
            <motion.div variants={item}>
              <Button type="submit" className="w-full" loading={submitting}>
                Sign in
              </Button>
            </motion.div>
          </form>
        </motion.div>

        {/* Footer */}
        <motion.p variants={item} className="mt-6 text-center text-[11px] uppercase tracking-[0.2em] text-white/35">
          ZYNX · by DurraniTech
        </motion.p>
      </motion.div>
    </div>
  );
}
